import { useIntl } from 'react-intl';
import { FarmData } from 'types/FarmData';
import { Button, Icon, Tooltip } from 'ui';
import { FallbackImage } from 'ui/UploadImage/fallBack/FallBackImage';
import CompanyType from 'enums/company-type';
import { qualityMarkLogoClassName } from 'utils/qualityMarkHelpers';
import { imgSrcParser } from 'utils/imgSrcParser';

interface FarmHeroProps {
  farm: FarmData;
  onProductsClick?: () => void;
}

interface RegistryItem {
  key: string;
  label: string;
  value: string;
}

interface SocialLink {
  key: string;
  href: string;
  title: string;
  logo?: string | null;
  icon?: string;
}

const withProtocol = (url: string) =>
  /^https?:\/\//i.test(url) ? url : `https://${url}`;

const telHref = (phone: string) => `tel:${phone.replace(/[\s()-]/g, '')}`;

const FarmHero = ({ farm, onProductsClick }: FarmHeroProps) => {
  const intl = useIntl();

  const title = farm.brand ?? farm.name;
  const address = farm.actual_full_address ?? farm.registered_full_address;
  const isCooperative =
    farm.company_type === CompanyType.COOPERATIVE_SOCIETY_KB;

  const registry: RegistryItem[] = [
    farm.reg_nr
      ? {
          key: 'reg',
          label: intl.formatMessage({ id: 'farm_view.reg_nr' }),
          value: farm.reg_nr,
        }
      : null,
    farm.pvd_nr
      ? {
          key: 'pvd',
          label: intl.formatMessage({ id: 'farm_view.pvd_nr' }),
          value: String(farm.pvd_nr),
        }
      : null,
    farm.pvn_nr
      ? {
          key: 'pvn',
          label: intl.formatMessage({ id: 'farm_view.pvn_nr' }),
          value: farm.pvn_nr,
        }
      : null,
  ].filter((item): item is RegistryItem => !!item);

  const socials: SocialLink[] = [
    ...(farm.social_networks ?? [])
      .filter((network) => !!network.pivot?.url)
      .map((network) => ({
        key: `social-${network.id}`,
        href: withProtocol(network.pivot.url),
        title: network.name,
        logo: network.logo,
      })),
    ...(farm.website
      ? [
          {
            key: 'website',
            href: withProtocol(farm.website),
            title: intl.formatMessage({ id: 'farm_view.website' }),
            icon: 'globe',
          },
        ]
      : []),
  ];

  const hasActions = !!farm.quality_marks?.length || !!onProductsClick;
  const hasContacts =
    !!farm.contact_phone || !!farm.contact_email || socials.length > 0;

  return (
    <div className={'farm-hero'}>
      <div className={'farm-hero-main'}>
        <div className={'farm-hero-logo'}>
          <FallbackImage
            src={farm.logo ?? farm.images?.[0]?.url_original}
            type={'FarmCard'}
            preview={false}
            alt={title}
          />
          {isCooperative && (
            <Tooltip
              title={intl.formatMessage({ id: 'farms.cooperative_society' })}
            >
              <span className={'farm-hero-cooperative'}>
                <img
                  src={'/qualityMark/3-09 1.jpg'}
                  alt={intl.formatMessage({ id: 'farms.cooperative_society' })}
                />
              </span>
            </Tooltip>
          )}
        </div>

        <div className={'farm-hero-text'}>
          <div className={'farm-hero-title-row'}>
            <h1 className={'farm-hero-title'}>{title}</h1>
            {farm.is_top_farm && (
              <span className={'farm-hero-top'}>
                {intl.formatMessage({ id: 'all_farm_card.favorite' })}
              </span>
            )}
          </div>
          <div className={'farm-hero-details'}>
            {!!address && <p className={'farm-hero-address'}>{address}</p>}
            {!!registry.length && (
              <p className={'farm-hero-registry'}>
                {registry.map((item) => (
                  <span key={item.key} className={'farm-hero-registry-item'}>
                    <span
                      className={'farm-hero-registry-separator'}
                      aria-hidden
                    >
                      |
                    </span>
                    <span>{item.label} </span>
                    <strong>{item.value}</strong>
                  </span>
                ))}
              </p>
            )}
          </div>
        </div>
      </div>

      {(hasActions || hasContacts) && (
        <div className={'farm-hero-aside'}>
          {hasActions && (
            <div className={'farm-hero-actions'}>
              {farm.quality_marks?.map((qmark) => (
                <Tooltip key={qmark.id} title={qmark.name}>
                  <span className={'farm-hero-qmark'}>
                    <FallbackImage
                      className={qualityMarkLogoClassName(qmark.slug)}
                      src={qmark.logo || ''}
                      type={'notFound'}
                      preview={false}
                      alt={qmark.name}
                    />
                  </span>
                </Tooltip>
              ))}
              {onProductsClick && (
                <Button
                  className={'farm-hero-products-btn'}
                  label={intl.formatMessage({ id: 'farm_view.products' })}
                  onClick={onProductsClick}
                />
              )}
            </div>
          )}

          {hasContacts && (
            <div className={'farm-hero-contacts'}>
              {!!farm.contact_phone && (
                <a
                  className={'farm-hero-pill'}
                  href={telHref(farm.contact_phone)}
                >
                  <span className={'farm-hero-pill-icon'}>
                    <Icon name={'call-calling'} type={'any'} />
                  </span>
                  {farm.contact_phone}
                </a>
              )}
              {!!farm.contact_email && (
                <a
                  className={'farm-hero-pill'}
                  href={`mailto:${farm.contact_email}`}
                >
                  <span className={'farm-hero-pill-icon'}>
                    <Icon name={'sms-tracking'} type={'any'} />
                  </span>
                  {farm.contact_email}
                </a>
              )}
              {socials.map((social) => (
                <a
                  key={social.key}
                  className={
                    social.icon || social.logo
                      ? 'farm-hero-social'
                      : 'farm-hero-pill'
                  }
                  href={social.href}
                  target={'_blank'}
                  rel={'noopener noreferrer'}
                  title={social.title}
                  aria-label={social.title}
                >
                  {social.icon ? (
                    <Icon name={social.icon} type={'any'} />
                  ) : social.logo ? (
                    <img src={imgSrcParser(social.logo)} alt={social.title} />
                  ) : (
                    social.title
                  )}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FarmHero;
